import { isGroupsEnabled } from "owa-account-capabilities/lib/isGroupsEnabled";
import { isAccountFeatureEnabled } from "owa-feature-flags";
import { getModuleForTasks, ModuleForTasks } from "owa-todo-module-utils";
import { M365AppId } from "./M365AppId";
import { getAppBposData } from "../utils/getAppBposData";
import isConsumer from "owa-session-store/lib/utils/isConsumer";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isFluidEnabledForSource, LoopType } from "owa-fluid-validations";
import type { MailboxInfo } from "owa-client-types";
import { isUnifiedAppStoreEnabled } from "../utils/isUnifiedAppStoreEnabled";
import isOutlookNewslettersEnabled from "owa-session-store/lib/utils/isOutlookNewslettersEnabled";
import { isToDoMetaOsEnabled } from "../utils/isToDoMetaOsEnabled";
import { isCommercialBizChatAvailable } from "../utils/isCommercialBizChatAvailable";
import { isOneDriveMetaOsEnabled, isOneDriveMetaOsConsumerEnabled, } from "../utils/isOneDriveMetaOsEnabled";
import { arePstTasksEnabled } from "../utils/arePstTasksEnabled";
import { isAccountExplicitLogon } from "owa-account-shared-mailbox-utils";

export const M365AcquisitionsInvariants: {
    [appId in M365AppId]: (mailboxInfo?: MailboxInfo) => boolean;
} = {
    // Outlook core modules
    [M365AppId.Mail]: () => true,
    [M365AppId.Calendar]: () => true,
    [M365AppId.People]: () => true,
    // Special case
    [M365AppId.AppStore]: (mailboxInfo?: MailboxInfo) => isUnifiedAppStoreEnabled(mailboxInfo),
    [M365AppId.AppStoreModal]: (mailboxInfo?: MailboxInfo) => isUnifiedAppStoreEnabled(mailboxInfo),
    [M365AppId.AppStoreAbout]: (mailboxInfo?: MailboxInfo) => isUnifiedAppStoreEnabled(mailboxInfo),
    [M365AppId.OrgExplorer]: () => true,
    [M365AppId.Groups]: (mailboxInfo?: MailboxInfo) => !!isGroupsEnabled(mailboxInfo),
    [M365AppId.Files]: () => false,
    [M365AppId.ToDo]: (mailboxInfo?: MailboxInfo) => getModuleForTasks(mailboxInfo ?? getGlobalSettingsAccountMailboxInfo()) ===
        ModuleForTasks.ToDo && !isToDoMetaOsEnabled(mailboxInfo),
    [M365AppId.ToDoMetaOs]: (mailboxInfo?: MailboxInfo) => getModuleForTasks(mailboxInfo ?? getGlobalSettingsAccountMailboxInfo()) ===
        ModuleForTasks.ToDo && isToDoMetaOsEnabled(mailboxInfo),
    [M365AppId.Tasks]: (mailboxInfo?: MailboxInfo) => arePstTasksEnabled(mailboxInfo),
    [M365AppId.MicrosoftPlaces]: () => true,
    [M365AppId.CommercialBizChat]: (mailboxInfo?: MailboxInfo) => isCommercialBizChatAvailable(mailboxInfo),
    // License dependent
    [M365AppId.ConsumerBizChat]: () => true,
    [M365AppId.Word]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.Word, mailboxInfo),
    [M365AppId.Excel]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.Excel, mailboxInfo),
    [M365AppId.PowerPoint]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.PowerPoint, mailboxInfo),
    [M365AppId.OneNote]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.OneNote, mailboxInfo),
    [M365AppId.VivaEngage]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.VivaEngage, mailboxInfo) &&
        isAccountFeatureEnabled("mos-builtinApps", mailboxInfo ?? getGlobalSettingsAccountMailboxInfo()),
    [M365AppId.Bookings]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.Bookings, mailboxInfo) &&
        isAccountFeatureEnabled("mos-builtinApps", mailboxInfo ?? getGlobalSettingsAccountMailboxInfo()),
    [M365AppId.OneDrive]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.OneDrive, mailboxInfo) &&
        !isOneDriveMetaOsEnabled(mailboxInfo) &&
        !isConsumer(undefined, mailboxInfo),
    [M365AppId.OneDriveMetaOs]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.OneDrive, mailboxInfo) &&
        isOneDriveMetaOsEnabled(mailboxInfo) &&
        !isConsumer(undefined, mailboxInfo),
    [M365AppId.OneDriveConsumerMetaOs]: (mailboxInfo?: MailboxInfo) => !!getAppBposData(M365AppId.OneDriveConsumerMetaOs, mailboxInfo) &&
        isOneDriveMetaOsConsumerEnabled(mailboxInfo) &&
        !isOneDriveMetaOsEnabled(mailboxInfo) &&
        isConsumer(undefined, mailboxInfo),
    [M365AppId.Hoop]: (mailboxInfo?: MailboxInfo) => isFluidEnabledForSource("MOS", mailboxInfo ?? getGlobalSettingsAccountMailboxInfo(), LoopType.Fluid),
    [M365AppId.Newsletters]: () => isOutlookNewslettersEnabled() &&
        !isAccountExplicitLogon(getGlobalSettingsAccountMailboxInfo()),
    [M365AppId.VsbAdmin]: () => true,
};
